import { useRef, useState, useCallback, useEffect } from 'react';

interface UseDockDragOptions {
  threshold?: number;
  onDismiss: () => void;
}

/**
 * Desktop-safe dock drag-to-dismiss.
 *
 * On touch: works via onTouchStart/Move/End on the handle element.
 * On mouse (desktop): attaches mousemove/mouseup to `window` so the drag
 * keeps tracking even when the cursor leaves the small handle area.
 */
export function useDockDrag({ threshold = 80, onDismiss }: UseDockDragOptions) {
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [isSlidingOut, setIsSlidingOut] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const isDragging = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const startDrag = useCallback((clientY: number) => {
    dragStartY.current = clientY;
    isDragging.current = false;
  }, []);

  const moveDrag = useCallback((clientY: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY - dragStartY.current;
    if (delta > 5) isDragging.current = true;
    setDragOffsetY(Math.max(0, delta));
  }, []);

  const endDrag = useCallback((clientY?: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY != null ? clientY - dragStartY.current : 0;
    const offset = Math.max(0, delta);
    dragStartY.current = null;

    if (offset >= threshold) {
      setIsSlidingOut(true);
      setDragOffsetY(999);
      onDismissRef.current();
    } else if (offset > 0) {
      setIsSnappingBack(true);
      setDragOffsetY(0);
      setTimeout(() => setIsSnappingBack(false), 200);
    }
  }, [threshold]);

  // ── Window-level mouse listeners for desktop ──
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (dragStartY.current === null) return;
      moveDrag(e.clientY);
    };
    const onMouseUp = (e: MouseEvent) => {
      if (dragStartY.current === null) return;
      endDrag(e.clientY);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [moveDrag, endDrag]);

  // Props to spread on the handle element
  const handleProps = {
    onTouchStart: (e: React.TouchEvent) => startDrag(e.touches[0].clientY),
    onTouchMove: (e: React.TouchEvent) => moveDrag(e.touches[0].clientY),
    onTouchEnd: (e: React.TouchEvent) => endDrag(e.changedTouches[0]?.clientY),
    onMouseDown: (e: React.MouseEvent) => startDrag(e.clientY),
    // mouse move/up handled at window level — no need on the element
  };

  const dragStyle = dragOffsetY > 0
    ? {
        transform: `translateY(${dragOffsetY}px)`,
        opacity: isSlidingOut ? 0 : Math.max(0.3, 1 - dragOffsetY / 200),
        transition: isSlidingOut
          ? 'transform 0.2s ease-in, opacity 0.2s ease-in'
          : 'none',
      }
    : isSnappingBack
      ? {
          transform: 'translateY(0px)',
          opacity: 1,
          transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
        }
      : undefined;

  return {
    dragOffsetY,
    isDragging,
    isSnappingBack,
    isSlidingOut,
    setIsSlidingOut,
    setDragOffsetY,
    handleProps,
    dragStyle,
    startDrag,
    endDrag,
  };
}
